import React from "react";
import { humanizeKey, PREVIEW_META_KEYS } from "./constants";
import LinkedValue from "./LinkedValue";

// Renders a single field value: booleans as Yes/No, blanks as an em-dash,
// everything else stringified.
function displayValue(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

// Preview fields whose value the server also links to a V1 page (the field name
// varies: MediaOutlet's V1 identity is v1_publication_id, the promo-entity types'
// is migrate_id — both point at preview.links.migrate_id).
const V1_LINKED_FIELDS = ["migrate_id", "v1_publication_id"];

function linkedFieldHref(key, links) {
  if (key === "slug") return links.slug;
  if (V1_LINKED_FIELDS.includes(key)) return links.migrate_id;
  return null;
}

/**
 * EntityPreviewCard — one side (source or destination) of the merge preview.
 *
 * `preview` is the per-type serialized shape from Admin::Api::MergesController
 * (fields vary by type). `differingKeys` highlights scalar fields whose value
 * differs from the other side so the operator can eyeball the diff.
 */
function EntityPreviewCard({ side, preview, differingKeys = [] }) {
  if (!preview) return null;

  const isSelfExpert = preview.self_expert === true;
  const sideLabel = side === "source" ? "Source (removed)" : "Destination (kept)";
  const headerClass = side === "source" ? "bg-danger-subtle" : "bg-success-subtle";

  const fieldEntries = Object.entries(preview).filter(([key]) => !PREVIEW_META_KEYS.includes(key));
  const links = preview.links || {};

  return (
    <div className="card h-100" data-testid={`preview-${side}`}>
      <div className={`card-header d-flex justify-content-between align-items-center ${headerClass}`}>
        <span className="fw-bold">{sideLabel}</span>
        {isSelfExpert && (
          <span className="badge bg-warning text-dark" data-testid={`self-expert-badge-${side}`}>
            <i className="fa fa-user-shield me-1" />
            Self-expert
          </span>
        )}
      </div>
      <div className="card-body p-0">
        <table className="table table-sm mb-0 align-middle">
          <tbody>
            <tr>
              <th scope="row" className="text-muted ps-3">
                ID
              </th>
              <td className="text-end pe-3">
                <LinkedValue href={links.id} testid={`preview-link-id-${side}`}>
                  {displayValue(preview.id)}
                </LinkedValue>
              </td>
            </tr>
            {fieldEntries.map(([key, value]) => {
              const highlighted = differingKeys.includes(key);
              const href = linkedFieldHref(key, links);
              return (
                <tr key={key} className={highlighted ? "table-warning" : undefined}>
                  <th scope="row" className="text-muted ps-3 fw-normal">
                    {humanizeKey(key)}
                  </th>
                  <td className="text-end pe-3">
                    <LinkedValue href={href} testid={`preview-link-${key}-${side}`}>
                      {displayValue(value)}
                    </LinkedValue>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EntityPreviewCard;
